// src/pages/dashboard/OrdersDashboard.jsx
// Tableau de bord des commandes en direct — route : /app/menus/:id/orders
// Style luxe (navy / or / serif) · glisser-déposer entre colonnes · icônes de plats · animations

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as Lucide from 'lucide-react'
import api from '../../lib/axios'

// Icônes sûres (déjà utilisées dans le projet)
const { ArrowLeft, Bell, BellOff, Loader2, Receipt, RefreshCw, X } = Lucide

// Icônes optionnelles : si une icône n'existe pas dans votre version de
// lucide-react, on retombe sur une icône de secours au lieu de planter.
const ico = (name, fallback = 'Circle') => Lucide[name] || Lucide[fallback] || Lucide.Circle

const GripVertical = ico('GripVertical', 'Menu')
const Clock = ico('Clock', 'Circle')
const Check = ico('Check', 'Circle')
const Trash = ico('Trash2', 'X')
const Utensils = ico('Utensils', 'UtensilsCrossed')

/* ------------------------------------------------------------------ */
/*  Constantes                                                         */
/* ------------------------------------------------------------------ */

const COLUMNS = [
  {
    id: 'new',
    label: 'Nouvelles',
    hint: 'À accepter',
    next: 'preparing',
    action: 'Préparer',
    accent: 'from-gold to-gold/30',
    dot: 'bg-gold',
    icon: ico('BellRing', 'Bell'),
  },
  {
    id: 'preparing',
    label: 'En préparation',
    hint: 'En cuisine',
    next: 'ready',
    action: 'Prête',
    accent: 'from-navy to-navy/30',
    dot: 'bg-navy',
    icon: ico('ChefHat', 'Utensils'),
  },
  {
    id: 'ready',
    label: 'Prêtes',
    hint: 'À servir',
    next: 'done',
    action: 'Servie',
    accent: 'from-emerald-500 to-emerald-300',
    dot: 'bg-emerald-500',
    icon: ico('ConciergeBell', 'Bell'),
  },
]

const POLL_MS = 8000

// Icône de plat selon le nom (FR / EN)
const DISH_RULES = [
  [/pizza|margherita|calzone/i, 'Pizza'],
  [/burger|sandwich|panini|tacos|wrap|kebab|shawarma|hot.?dog/i, 'Sandwich'],
  [/salad/i, 'Salad'],
  [/soup|harira|velout|bouillon/i, 'Soup'],
  [/poisson|fish|saumon|salmon|thon|crevette|shrimp|sushi|calamar|dorade|sole/i, 'Fish'],
  [/poulet|chicken|dinde|turkey|nugget|wings/i, 'Drumstick'],
  [/boeuf|bœuf|beef|steak|viande|entrec|agneau|lamb|tajine|tagine|brochette|kefta|merguez/i, 'Beef'],
  [/oeuf|œuf|egg|omelette/i, 'Egg'],
  [/croissant|pain|bread|viennoiserie|msemen|baghrir|baguette/i, 'Croissant'],
  [/glace|ice.?cream|sorbet/i, 'IceCreamCone'],
  [/g[âa]teau|cake|tarte|tart|dessert|fondant|brownie|mousse|cr[êe]pe|gaufre|waffle|tiramisu|cheesecake/i, 'Cake'],
  [/cookie|biscuit|macaron/i, 'Cookie'],
  [/caf[ée]|coffee|espresso|cappuccino|latte|nescaf|infusion|(^|[^a-zà-ÿ])(th[eé]|tea)(?![a-zà-ÿ])/i, 'Coffee'],
  [/vin|wine|champagne/i, 'Wine'],
  [/bi[èe]re|beer/i, 'Beer'],
  [/cocktail|mojito|martini|spritz/i, 'Martini'],
  [/jus|juice|smoothie|citron|orange|fruit|avocat|fraise/i, 'Cherry'],
  [/eau|water|soda|coca|boisson|limonade|sprite|fanta|perrier/i, 'GlassWater'],
  [/p[âa]tes|pasta|spaghetti|tagliatelle|lasagne|riz|rice|couscous|risotto/i, 'Wheat'],
]

const iconCache = new Map()
function dishIcon(name = '') {
  const key = String(name)
  if (iconCache.has(key)) return iconCache.get(key)
  const rule = DISH_RULES.find(([re]) => re.test(key))
  const Cmp = ico(rule ? rule[1] : 'Utensils', 'Utensils')
  iconCache.set(key, Cmp)
  return Cmp
}

/* ------------------------------------------------------------------ */
/*  Utilitaires                                                        */
/* ------------------------------------------------------------------ */

const money = (n, currency = 'DH') =>
  `${Number.isInteger(n) ? n : Number(n).toFixed(2)} ${currency}`

const toDate = (iso) =>
  new Date(String(iso).includes('T') ? iso : `${String(iso).replace(' ', 'T')}Z`)

const ageMin = (iso) =>
  Math.max(0, Math.round((Date.now() - toDate(iso).getTime()) / 60000))

function timeAgo(iso) {
  const m = ageMin(iso)
  if (m < 1) return "à l'instant"
  if (m < 60) return `il y a ${m} min`
  return `il y a ${Math.floor(m / 60)} h ${m % 60} min`
}

function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext
    const ctx = new Ctx()
    ;[880, 1175].forEach((freq, i) => {
      const o = ctx.createOscillator()
      const g = ctx.createGain()
      o.frequency.value = freq
      o.connect(g)
      g.connect(ctx.destination)
      const t = ctx.currentTime + i * 0.18
      g.gain.setValueAtTime(0.0001, t)
      g.gain.exponentialRampToValueAtTime(0.25, t + 0.02)
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16)
      o.start(t)
      o.stop(t + 0.18)
    })
    setTimeout(() => ctx.close(), 600)
  } catch {
    /* ignore */
  }
}

/* ------------------------------------------------------------------ */
/*  Animations                                                         */
/* ------------------------------------------------------------------ */

const STYLES = `
@keyframes od-in { from { opacity: 0; transform: translateY(10px) scale(.94) } to { opacity: 1; transform: none } }
@keyframes od-glow { 0%,100% { box-shadow: 0 0 0 0 rgba(201,162,39,0) } 50% { box-shadow: 0 0 0 7px rgba(201,162,39,.28) } }
@keyframes od-pop { 0% { transform: scale(.7) } 60% { transform: scale(1.15) } 100% { transform: scale(1) } }
@keyframes od-slide { from { opacity: 0; transform: translateY(16px) } to { opacity: 1; transform: none } }
.od-in { animation: od-in .5s cubic-bezier(.2,.8,.2,1) both }
.od-glow { animation: od-glow 1.4s ease-in-out 4 }
.od-pop { animation: od-pop .45s cubic-bezier(.2,.8,.2,1) both }
.od-slide { animation: od-slide .35s cubic-bezier(.2,.8,.2,1) both }
@media (prefers-reduced-motion: reduce) {
  .od-in, .od-glow, .od-pop, .od-slide { animation: none }
}
`

function GoldRule({ className = '' }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="h-px w-8 bg-gradient-to-r from-gold to-gold/0" />
      <span className="w-1.5 h-1.5 rotate-45 bg-gold" />
      <span className="h-px w-8 bg-gradient-to-l from-gold to-gold/0" />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Carte de commande                                                  */
/* ------------------------------------------------------------------ */

function OrderCard({
  order,
  column,
  busy,
  flash,
  dragging,
  draggable,
  onDragStart,
  onDragEnd,
  onStatus,
}) {
  const minutes = ageMin(order.created_at)
  const urgent = column && minutes >= 20
  const warn = column && minutes >= 10 && !urgent

  const uniqueIcons = []
  order.items.forEach((it) => {
    const Cmp = dishIcon(it.name)
    if (!uniqueIcons.includes(Cmp)) uniqueIcons.push(Cmp)
  })
  const shown = uniqueIcons.slice(0, 4)
  const extra = uniqueIcons.length - shown.length

  return (
    <article
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={[
        'od-in group relative bg-white rounded-2xl border p-4 space-y-3 shadow-sm',
        'transition-all duration-300 hover:shadow-xl hover:shadow-navy/10 hover:-translate-y-0.5 hover:border-gold/50',
        draggable ? 'cursor-grab active:cursor-grabbing select-none' : '',
        dragging ? 'opacity-40 scale-95 rotate-1 border-gold' : 'border-gray-100',
        flash ? 'od-glow border-gold' : '',
      ].join(' ')}
    >
      {draggable && (
        <span
          className="absolute top-3 right-3 text-gray-300 group-hover:text-gold transition-colors"
          title="Glisser vers une autre colonne"
        >
          <GripVertical className="w-4 h-4" />
        </span>
      )}

      <header className="flex items-start justify-between gap-3 pr-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-serif text-lg text-navy leading-none">N° {order.number}</span>
            {order.table ? (
              <span className="px-2 py-0.5 rounded-full border border-gold/50 text-[10px] font-semibold uppercase tracking-[0.15em] text-gold">
                Table {order.table}
              </span>
            ) : null}
          </div>
          <div className="mt-1 text-[11px] text-gray-400 truncate">
            {order.name ? `${order.name} · ` : ''}
            {timeAgo(order.created_at)}
          </div>
        </div>
        <div className="font-serif text-base text-navy shrink-0">
          {money(order.total, order.currency)}
        </div>
      </header>

      {/* icônes des plats */}
      <div className="flex items-center gap-1.5">
        {shown.map((Cmp, i) => (
          <span
            key={i}
            className="od-pop w-9 h-9 rounded-full border border-gold/40 bg-gold/5 text-gold flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:bg-navy group-hover:border-navy"
            style={{ animationDelay: `${i * 70}ms` }}
          >
            <Cmp className="w-4 h-4" />
          </span>
        ))}
        {extra > 0 && (
          <span className="w-9 h-9 rounded-full bg-graysoft text-[11px] font-semibold text-gray-500 flex items-center justify-center">
            +{extra}
          </span>
        )}
      </div>

      <ul className="space-y-1.5 border-t border-gray-100 pt-3">
        {order.items.map((it, i) => {
          const Cmp = dishIcon(it.name)
          return (
            <li key={i} className="text-xs text-gray-700">
              <div className="flex items-center gap-2">
                <Cmp className="w-3.5 h-3.5 text-gold shrink-0 transition-transform duration-300 hover:scale-125" />
                <span className="font-semibold text-navy tabular-nums">{it.qty} ×</span>
                <span className="truncate">{it.name}</span>
              </div>
              {it.note ? (
                <div className="text-[11px] text-gray-400 pl-[22px] mt-0.5">↳ {it.note}</div>
              ) : null}
            </li>
          )
        })}
      </ul>

      {order.note ? (
        <p className="text-[11px] bg-gold/10 border border-gold/30 rounded-xl px-3 py-2 text-navy/80">
          {order.note}
        </p>
      ) : null}

      {column && (
        <div className="flex items-center gap-2 pt-1">
          <span
            className={[
              'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold tabular-nums',
              urgent
                ? 'bg-red-50 text-red-600 animate-pulse'
                : warn
                ? 'bg-amber-50 text-amber-700'
                : 'bg-graysoft text-gray-500',
            ].join(' ')}
            title="Temps écoulé"
          >
            <Clock className="w-3 h-3" />
            {minutes} min
          </span>

          <button
            disabled={busy}
            onClick={() => onStatus(order.id, column.next)}
            className="flex-1 py-2.5 rounded-full bg-navy text-white text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-navy/90 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-60"
          >
            {column.action}
          </button>

          <button
            disabled={busy}
            onClick={() => window.confirm('Annuler cette commande ?') && onStatus(order.id, 'cancelled')}
            aria-label="Annuler la commande"
            className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 hover:scale-110 active:scale-95 transition-all disabled:opacity-60"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </article>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function OrdersDashboard() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [orders, setOrders] = useState([])
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [view, setView] = useState('board') // board | history
  const [sound, setSound] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [flashIds, setFlashIds] = useState(() => new Set())
  const [dragId, setDragId] = useState(null)
  const [overZone, setOverZone] = useState(null)
  const [, setTick] = useState(0)

  const knownIds = useRef(null) // null = premier chargement (pas de bip)
  const soundRef = useRef(sound)
  soundRef.current = sound

  const load = useCallback(async () => {
    try {
      const [ordersRes, reqRes] = await Promise.all([
        api.get(`/pages/${id}/orders`),
        api.get(`/pages/${id}/service-requests`).catch(() => ({ data: [] })),
      ])
      const list = ordersRes.data?.data ?? ordersRes.data ?? []
      const reqs = reqRes.data?.data ?? reqRes.data ?? []
      if (knownIds.current) {
        const fresh = list.filter((o) => o.status === 'new' && !knownIds.current.has(o.id))
        const freshReq = reqs.filter((r) => !knownIds.current.has(`req-${r.id}`))
        if ((fresh.length || freshReq.length) && soundRef.current) beep()
        if (fresh.length) {
          document.title = `(${fresh.length}) Nouvelle commande`
          const ids = fresh.map((o) => o.id)
          setFlashIds((cur) => new Set([...cur, ...ids]))
          setTimeout(() => {
            setFlashIds((cur) => {
              const next = new Set(cur)
              ids.forEach((x) => next.delete(x))
              return next
            })
          }, 6000)
        }
      }
      knownIds.current = new Set([...list.map((o) => o.id), ...reqs.map((r) => `req-${r.id}`)])
      setOrders(list)
      setRequests(reqs)
      setError('')
    } catch (err) {
      console.error(err)
      setError('Connexion perdue — nouvelle tentative…')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    load()
    const t = setInterval(load, POLL_MS)
    const clock = setInterval(() => setTick((n) => n + 1), 30000)
    const onFocus = () => {
      document.title = 'Commandes'
      load()
    }
    window.addEventListener('focus', onFocus)
    return () => {
      clearInterval(t)
      clearInterval(clock)
      window.removeEventListener('focus', onFocus)
    }
  }, [load])

  const setStatus = async (orderId, status) => {
    setBusyId(orderId)
    // mise à jour optimiste
    setOrders((cur) => cur.map((o) => (o.id === orderId ? { ...o, status } : o)))
    try {
      await api.patch(`/orders/${orderId}`, { status })
    } catch (err) {
      console.error(err)
      setError('Mise à jour impossible.')
      load()
    } finally {
      setBusyId(null)
    }
  }

  const resolveRequest = async (requestId) => {
    setRequests((cur) => cur.filter((r) => r.id !== requestId))
    try {
      await api.patch(`/service-requests/${requestId}`)
    } catch (err) {
      console.error(err)
      setError('Mise à jour impossible.')
      load()
    }
  }

  /* ---- glisser-déposer ---- */
  const startDrag = (orderId) => (e) => {
    setDragId(orderId)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(orderId))
  }

  const endDrag = () => {
    setDragId(null)
    setOverZone(null)
  }

  const dropOn = (zoneId) => {
    const order = orders.find((o) => String(o.id) === String(dragId))
    endDrag()
    if (!order || order.status === zoneId) return
    if (zoneId === 'cancelled' && !window.confirm('Annuler cette commande ?')) return
    setStatus(order.id, zoneId)
  }

  const zoneProps = (zoneId) => ({
    onDragOver: (e) => {
      if (dragId == null) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      if (overZone !== zoneId) setOverZone(zoneId)
    },
    onDragLeave: (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        setOverZone((z) => (z === zoneId ? null : z))
      }
    },
    onDrop: (e) => {
      e.preventDefault()
      dropOn(zoneId)
    },
  })

  const stats = useMemo(() => {
    const valid = orders.filter((o) => o.status !== 'cancelled')
    return {
      count: valid.length,
      revenue: valid.reduce((s, o) => s + Number(o.total || 0), 0),
      currency: orders[0]?.currency || 'DH',
      pending: orders.filter((o) => ['new', 'preparing'].includes(o.status)).length,
    }
  }, [orders])

  const history = orders.filter((o) => o.status === 'done' || o.status === 'cancelled')

  const statCards = [
    ['Aujourd’hui', stats.count, false],
    ['En cours', stats.pending, false],
    ['Chiffre', money(stats.revenue, stats.currency), true],
  ]

  return (
    <div className="min-h-dvh bg-gradient-to-b from-white to-graysoft/50">
      <style>{STYLES}</style>

      {/* EN-TÊTE */}
      <header className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-gray-100 px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <button
          onClick={() => navigate(`/app/menus/${id}/edit`)}
          className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500 hover:text-navy transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Menu
        </button>

        <div className="text-center">
          <p className="text-[10px] uppercase tracking-[0.35em] text-gold font-semibold">Service en salle</p>
          <h1 className="font-serif text-lg sm:text-xl text-navy leading-tight">Commandes en direct</h1>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={load}
            aria-label="Actualiser"
            className="w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gold hover:bg-gold/10 hover:rotate-90 transition-all duration-300"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              setSound((s) => !s)
              if (!sound) beep()
            }}
            aria-pressed={sound}
            aria-label="Son des nouvelles commandes"
            className={`w-9 h-9 flex items-center justify-center rounded-full transition-all ${
              sound ? 'text-gold bg-gold/10' : 'text-gray-400 hover:bg-gray-100'
            }`}
          >
            {sound ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* STATS */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          {statCards.map(([label, value, gold], i) => (
            <div
              key={label}
              className="od-in relative overflow-hidden bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <span className="absolute top-0 left-0 h-px w-full bg-gradient-to-r from-gold/0 via-gold to-gold/0" />
              <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-semibold">{label}</div>
              <div className={`mt-2 font-serif text-2xl sm:text-3xl tabular-nums ${gold ? 'text-gold' : 'text-navy'}`}>
                {value}
              </div>
            </div>
          ))}
        </div>

        {/* DEMANDES DE SERVICE */}
        {requests.length > 0 && (
          <div className="space-y-2" role="status">
            {requests.map((r) => (
              <div
                key={r.id}
                className="od-slide flex items-center justify-between gap-3 rounded-2xl border border-gold/40 bg-gradient-to-r from-gold/10 to-white pl-4 pr-3 py-3 shadow-sm"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 shrink-0 rounded-full bg-navy text-gold flex items-center justify-center animate-pulse">
                    {r.type === 'bill' ? <Receipt className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-navy truncate">
                      Table {r.table} — {r.type === 'bill' ? "demande l'addition" : 'appelle le serveur'}
                    </p>
                    <p className="text-[11px] text-gray-400">{timeAgo(r.created_at)}</p>
                  </div>
                </div>
                <button
                  onClick={() => resolveRequest(r.id)}
                  className="shrink-0 px-4 py-2 rounded-full bg-navy text-white text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-navy/90 hover:scale-105 active:scale-95 transition-all"
                >
                  Traité
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ONGLETS */}
        <div className="inline-flex p-1 rounded-full bg-white border border-gray-100 shadow-sm">
          {[
            ['board', 'En cours'],
            ['history', `Historique (${history.length})`],
          ].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-5 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.18em] transition-all duration-300 ${
                view === v ? 'bg-navy text-white shadow-md' : 'text-gray-500 hover:text-navy'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="od-slide p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600">{error}</div>
        )}

        {loading ? (
          <div className="py-24 flex justify-center">
            <Loader2 className="w-8 h-8 text-gold animate-spin" />
          </div>
        ) : view === 'board' ? (
          <>
            <div className="grid gap-5 md:grid-cols-3">
              {COLUMNS.map((col) => {
                const list = orders
                  .filter((o) => o.status === col.id)
                  .sort((a, b) => a.number - b.number)
                const ColIcon = col.icon
                const isOver = overZone === col.id && dragId != null
                return (
                  <section
                    key={col.id}
                    {...zoneProps(col.id)}
                    className={[
                      'relative rounded-3xl border p-4 space-y-4 transition-all duration-300',
                      isOver
                        ? 'border-gold bg-gold/5 ring-4 ring-gold/20 scale-[1.01]'
                        : 'border-gray-100 bg-white/70',
                      dragId != null && !isOver ? 'border-dashed' : '',
                    ].join(' ')}
                  >
                    <span className={`absolute top-0 left-6 right-6 h-0.5 rounded-full bg-gradient-to-r ${col.accent}`} />

                    <h2 className="flex items-center justify-between pt-1">
                      <span className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-full border border-gold/40 bg-white text-gold flex items-center justify-center">
                          <ColIcon className="w-4 h-4" />
                        </span>
                        <span>
                          <span className="block font-serif text-base text-navy leading-tight">{col.label}</span>
                          <span className="block text-[10px] uppercase tracking-[0.22em] text-gray-400">{col.hint}</span>
                        </span>
                      </span>
                      <span
                        key={list.length}
                        className="od-pop min-w-[28px] h-7 px-2 rounded-full bg-navy text-white text-xs font-semibold flex items-center justify-center tabular-nums"
                      >
                        {list.length}
                      </span>
                    </h2>

                    {list.length === 0 && (
                      <div
                        className={`rounded-2xl border border-dashed py-10 text-center transition-colors ${
                          isOver ? 'border-gold text-gold' : 'border-gray-200 text-gray-300'
                        }`}
                      >
                        <Utensils className="w-6 h-6 mx-auto mb-2" />
                        <p className="text-[11px] uppercase tracking-[0.2em]">
                          {isOver ? 'Déposer ici' : 'Aucune commande'}
                        </p>
                      </div>
                    )}

                    {list.map((o) => (
                      <OrderCard
                        key={o.id}
                        order={o}
                        column={col}
                        busy={busyId === o.id}
                        flash={flashIds.has(o.id)}
                        dragging={String(dragId) === String(o.id)}
                        draggable
                        onDragStart={startDrag(o.id)}
                        onDragEnd={endDrag}
                        onStatus={setStatus}
                      />
                    ))}
                  </section>
                )
              })}
            </div>

            {/* ZONES DE DÉPÔT : Servie / Annulée (visibles pendant le glissement) */}
            {dragId != null && (
              <div className="od-slide fixed bottom-4 left-1/2 -translate-x-1/2 z-30 flex gap-3 w-[min(92vw,34rem)]">
                {[
                  ['done', 'Servie', Check, 'text-emerald-600 border-emerald-300 bg-emerald-50'],
                  ['cancelled', 'Annuler', Trash, 'text-red-600 border-red-300 bg-red-50'],
                ].map(([zone, label, Icon, tone]) => (
                  <div
                    key={zone}
                    {...zoneProps(zone)}
                    className={[
                      'flex-1 flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed py-4 shadow-xl backdrop-blur transition-all duration-300',
                      overZone === zone ? `${tone} scale-105 border-solid` : 'bg-white/90 border-gray-300 text-gray-500',
                    ].join(' ')}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em]">{label}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {history.length === 0 && (
              <div className="col-span-full text-center py-16">
                <GoldRule className="justify-center mb-4" />
                <p className="font-serif text-lg text-gray-400">Rien pour le moment.</p>
              </div>
            )}
            {history.map((o) => (
              <div key={o.id} className={o.status === 'cancelled' ? 'opacity-60' : ''}>
                <OrderCard order={o} column={null} busy={false} onStatus={setStatus} />
                <div className="mt-2 px-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${o.status === 'cancelled' ? 'bg-red-400' : 'bg-emerald-500'}`}
                  />
                  <span className={o.status === 'cancelled' ? 'text-red-400' : 'text-emerald-600'}>
                    {o.status === 'cancelled' ? 'Annulée' : 'Servie'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}